import {useContext, useEffect, useState, useMemo, useCallback, useRef} from "react";
import { ConfiguratorContext } from "../../context";
import {
    Button,
    Calendar,
    DatePicker,
    InputRef,
    notification, Popover,
    Segmented,
    Select,
    Skeleton,
    Space,
    Table,
    TableColumnType,
    TablePaginationConfig, Tooltip,
} from "antd";
import {
    DEFAULT_THROTTLE,
    PAGINATION_MAX_PAGE_SIZE,
    Permission, scheduledTruck,
    ScheduledTruckDto,
    ScheduleFilterOptionDto,
    SortDirection, Truck,
    TruckDateFilterType, TruckDto,
    TruckFilter, UploadHistory
} from "../../api/models";
import Title from "antd/es/typography/Title";
import dayjs, { Dayjs } from "dayjs";
import TruckFilterControls from "../../components/TruckFilterControls";
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { CalendarProps } from "antd/lib";
import { Link } from "react-router-dom";
import { useAsyncState } from "../../hook/useAsyncState";
import { useIntl } from "react-intl";
import { getCSVRow } from "../../helpers/csv";
import { CalendarOutlined, UnorderedListOutlined  } from "@ant-design/icons";
import { ArrayParam, BooleanParam, NumberParam, NumericArrayParam, StringParam, useQueryParam } from "use-query-params";
import weekday from 'dayjs/plugin/weekday';
import updateLocale from 'dayjs/plugin/updateLocale';
import { FilterValue, SorterResult } from "antd/es/table/interface";
import useMasterSchedule from "../../swr/useMasterSchedule";
import useMasterScheduleCalendar from "../../swr/useMasterScheduleCalendar";
import {throttle} from "lodash";
import useMasterScheduleUploadHistory from "../../swr/useMasterScheduleUploadHistory";
import BMButton from "../../components/BMButton";
import {Utils} from "../../util/util";

const NOT_FOUND = -1;

dayjs.extend(utc);
dayjs.extend(timezone);

const plantTz = 'America/New_York';

dayjs.extend(weekday);
dayjs.extend(updateLocale);
// Update the locale to for week day name
dayjs.updateLocale('en', {
  weekdaysMin: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'], // Customize this array as needed
});

enum CalendarViewDate {
    PRODUCTION, SHIPPING
}

const MASTER_SCHEDULE_UPLOAD_SUCCESSFUL = "Successful";

function getDateFilterStartEndAround(date: Dayjs) {
  return [
    date.tz(plantTz, true).startOf('month').subtract(6, 'day'),
    date.tz(plantTz, true).endOf('month').add(6, 'day'),
  ];
}

const DEFAULT_PAGE_SORT = {
  field:  'projectedProductionDate',
  direction: 'asc' as SortDirection,
  order: 'ascend',
}

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_FILTER:TruckFilter = {
  dealerLst: [],
  dateFilterType: TruckDateFilterType.PRODUCTION,
  dateFilterStart: dayjs(new Date()).tz(plantTz, true).startOf('month'),
  dateFilterEnd: dayjs(new Date()).add(10, 'year').tz(plantTz, true)
};

const EXCLAMATION_DELIMITER = "!!";

export interface TruckCalendarDto {
  id: number;
  truckSerialNumberStr: string;
  productionDate?: Date | null | undefined;
  shipDate?: Date | null | undefined;
  endCustomer: string;
  quoteId: string;
}

const ScheduleCalendarView = () => {

    const [filter, setFilter] = useState<TruckFilter>( {
      ...DEFAULT_FILTER,
    });

    const calendarTrucks = useMasterScheduleCalendar({
        ...filter,
        dateFilterStart: filter.dateFilterStart?.toDate(),
        dateFilterEnd: filter.dateFilterEnd?.toDate(),
        page: 0,
        size: PAGINATION_MAX_PAGE_SIZE,
        sort:DEFAULT_PAGE_SORT
    })

    const [value, setValue] = useState<Dayjs>();
    const [calendarViewDate, setCalendarViewDate] = useState(CalendarViewDate.PRODUCTION);

    const onPanelChange = useCallback(throttle((value: Dayjs, _mode: CalendarProps<Dayjs>['mode']) => {
        const [dateFilterStart, dateFilterEnd] = getDateFilterStartEndAround(value);
        setFilter({ ...filter, dateFilterStart, dateFilterEnd })
        setValue(value);
    }, DEFAULT_THROTTLE), []);


    const listData = calendarTrucks?.data?.content
        .reduce( (acc,t) => {

      const prodDate = dayjs(t.productionDate).tz('utc').tz(plantTz, true).startOf('day');
      const shipDate = dayjs(t.shipDate).tz('utc').tz(plantTz, true).startOf('day');
      const displayDate = calendarViewDate == CalendarViewDate.PRODUCTION ? prodDate : shipDate;

      const dateNdx = displayDate.format("MM/DD/YYYY"); 
      acc[ dateNdx ] = (acc[ dateNdx ] || []).concat(t);
      return acc;

    }, {} as Record<string, TruckCalendarDto[]>);

    const dateCellRender = (value: Dayjs) => {

      const lst = listData?.[ value.format("MM/DD/YYYY" ) ];
      return (
        /* TODO - in future add some styling to reflect how full a slot is */
        /*<div style={{ backgroundColor: listData.length > 2 ? 'rgba(255, 0, 0, 0.3)' : '' }}>*/

        <div className="custom-calendar-cell">

          <ul style={{ listStyleType: 'none', padding: 0, fontSize: "10px" }}>
            {lst?.map((item) => (
              <li key={item.id}>
                <Link to={'/configurator/' + item.quoteId} target="_new">
                  {item.truckSerialNumberStr} ({item.endCustomer})
                </Link>
              </li>
            ))}
          </ul>
        </div>
      );
    };

    const cellRender: CalendarProps<Dayjs>['cellRender'] = (current, info) => {
        if (info.type === 'date') return dateCellRender(current);
        return info.originNode;
    };

    return (
      <>
        <style>
          {`
            .custom-calendar-cell {
                  overflow-y: auto;
                  max-height: 100px;
                  padding-right: 12px;
                  width: 100%;
              }

              .custom-calendar-cell::-webkit-scrollbar {
                  width: 8px;
              }

              .custom-calendar-cell::-webkit-scrollbar-track {
                  background: #e1e9eb;
              }

              .custom-calendar-cell::-webkit-scrollbar-thumb {
                  background-color: #c9cecf;
                  border-radius: 4px;
                  border: 1px solid #c9cecf;
              }

              .custom-calendar-cell::-webkit-scrollbar-thumb:hover {
                  background-color: darkgrey;
              }

              /* Hide overflow in the calendar cells */
              .ant-picker-calendar-date-content {
                  overflow: hidden !important;
              }
                      
          `}
        </style>
        <Skeleton active loading={calendarTrucks.isLoading || !calendarTrucks?.data} style={{marginTop: "1rem"}}>
          <Select style={{ width: '300px' }} placeholder="Select date to view" value={calendarViewDate} onChange={setCalendarViewDate}>
              <Select.Option key={CalendarViewDate.PRODUCTION} value={CalendarViewDate.PRODUCTION}>Production Date</Select.Option>
              <Select.Option key={CalendarViewDate.SHIPPING} value={CalendarViewDate.SHIPPING}>Ship Date</Select.Option>
          </Select>
          <style>
            {`
              .ms-calendar .ant-picker-calendar-mode-switch {
                display:none;
              }

              .ms-calendar .ant-picker-content th:nth-child(1), /* Sunday header */
              .ms-calendar .ant-picker-content th:nth-child(7), /* Saturday header */
              .ms-calendar .ant-picker-cell:nth-child(7n+1), /* Sunday cells */
              .ms-calendar .ant-picker-cell:nth-child(7n) { /* Saturday cells */
              }
              
            `}
          </style>
          <Calendar style={{marginTop: "-2rem"}} className="ms-calendar" value={value} cellRender={cellRender} onPanelChange={onPanelChange} />
        </Skeleton>
      </>
    );
};

const MasterScheduleView = () => {
    const intl = useIntl();
    const configuratorContext = useContext(ConfiguratorContext);

    const [searchFilterParam, setSearchFilterParam] = useQueryParam<string | undefined | null>("filter", StringParam);
    const [dealerFilterParam, setDealerFilterParam] = useQueryParam<Array<string | null> | undefined | null>("dealer", ArrayParam);
    const [salespersonsParam, setSalespersonsParam] = useQueryParam<Array<string | null> | undefined | null>("salespersons", ArrayParam);
    const [endCustomerParam, setEndCustomerParam] = useQueryParam<Array<number | null> | undefined | null>("endCustomerId", NumericArrayParam);
    const [isPilotInspectionParam, setIsPilotInspectionParam] = useQueryParam<boolean | undefined | null>("isPilotInspection", BooleanParam);

    const [sortFieldsParam, setSortFieldsParam] = useQueryParam<string | undefined | null>("fields", StringParam);
    const [sortDirectionsParam, setSortDirectionsParam] = useQueryParam<string | undefined | null>("directions", StringParam);
    const [filterFieldsParam, setFilterFieldsParam] = useQueryParam<string | undefined | null>("filterFields", StringParam);
    const [filterValueNumbersParam, setFilterValueNumbersParam] = useQueryParam<string | null | undefined>("fnr", StringParam);
    const [filterValuesParam, setFilterValuesParam] = useQueryParam<string | undefined | null>("filterValues", StringParam);

    const [pageSizeQueryParam, setPageSizeQueryParam] = useQueryParam<number | null | undefined>("nr", NumberParam);
    const [currentPageParam, setCurrentPageParam] = useQueryParam<number | null | undefined>("p", NumberParam);

    const [currentSorting, setCurrentSorting] = useState<any>({ columnKey: DEFAULT_PAGE_SORT.field, order: DEFAULT_PAGE_SORT.order });
    const [currentFilter, setCurrentFilter] = useState<any>({});

    const [filterOptions, filterOptionsAsyncState] = useAsyncState<ScheduleFilterOptionDto>();

    const [pagination, setPagination] = useState<TablePaginationConfig>({
        pageSize: pageSizeQueryParam == null || pageSizeQueryParam > 500 ? DEFAULT_PAGE_SIZE : pageSizeQueryParam,
        current: currentPageParam == null || currentPageParam < 1 ? 1 : currentPageParam,
    });

    const LIST_DEFAULT_FILTER = {
      ...DEFAULT_FILTER,
      search: searchFilterParam || undefined, //silly fix for null
      dealerLst: (dealerFilterParam || undefined) as (string[] | undefined), //silly fix for null
      salespersons: [salespersonsParam as (string|null) || []].flat(),
      endCustomerId: endCustomerParam as number[] || undefined,
      isPilotInspection: isPilotInspectionParam || undefined,
      isMasterSchedule: true,
      sortFields: sortFieldsParam || 'projectedProductionDate',
      sortDirections: sortDirectionsParam || 'asc',
      filterFields: filterFieldsParam || undefined,
      filterValueNumbers: filterValueNumbersParam || undefined,
      filterValues: filterValuesParam || undefined,
    }

    const [filter, setFilter] = useState<TruckFilter>( {
      ...LIST_DEFAULT_FILTER
    });
    const [showCalendarView, setShowCalendarView] = useState<number>(0);

    const scheduleAsync = useMasterSchedule({
        //temp fix
        sortFields: sortFieldsParam || 'projectedProductionDate',
        sortDirections: sortDirectionsParam || 'asc',
        ...filter,
        dateFilterStart: filter.dateFilterStart?.toDate(),
        dateFilterEnd: filter.dateFilterEnd?.toDate(),
        page: (pagination.current || 1) - 1,
        size: pagination.pageSize || DEFAULT_PAGE_SIZE,
    })

    const uploadHistoryAsync = useMasterScheduleUploadHistory();

    useEffect(() => {
      setSortDirectionsParam("asc");
      setSortFieldsParam("projectedProductionDate");
    }, []);

    useEffect(() => {
      if (!showCalendarView) {
        loadFilterOptions(pagination, filter);
      }
    }, [pagination.pageSize, pagination.current, filter]);

    const onFilterChange = useCallback( throttle( (_values: Record<string, any>, filter: TruckFilter) => {
        var filter1: any = { ...filter, search: filter.search == '' ? undefined : filter.search };
        filter1 = filter['dateRange'] ? { ...filter, dateFilterStart: filter['dateRange'][0], dateFilterEnd: filter['dateRange'][1] } : filter1;
        filter1 = {...filter1, isMasterSchedule: true};
        setFilter(filter1);
        setPagination({ ...pagination, current: 1 });

        setDealerFilterParam(filter.dealerLst);
        setSearchFilterParam(filter.search);
        setSalespersonsParam(filter.salespersons);
        setIsPilotInspectionParam(filter.isPilotInspection);
        !!filter.endCustomerId?.length && setEndCustomerParam(filter.endCustomerId);
    }, DEFAULT_THROTTLE), []);

    const handleToggleCalendarView = () => {

      const nextCalendarView = !showCalendarView;
      const dateFilter = ( nextCalendarView ) 
        ? {
          dateFilterStart: getDateFilterStartEndAround(dayjs(new Date()))[0],
          dateFilterEnd: getDateFilterStartEndAround(dayjs(new Date()))[1],
        }
        : {
          dateFilterStart: DEFAULT_FILTER.dateFilterStart,
          dateFilterEnd: DEFAULT_FILTER.dateFilterEnd
        };

      const pageSize = (nextCalendarView ) ? PAGINATION_MAX_PAGE_SIZE : DEFAULT_PAGE_SIZE;
      setPagination({
        ...pagination,
        pageSize ,
      });

      setFilter({
        ...filter,
        ...dateFilter
      });

      setShowCalendarView(!showCalendarView ? 1 : 0);

      //re-init scheduleAsync for cleaner transition
      // scheduleAsync.setInit();
    }

    const loadFilterOptions = async (pagination: TablePaginationConfig, filter:TruckFilter) => {
      filterOptionsAsyncState.setLoading();
      try {
        const page = pagination.current || 1;
        const resp = await configuratorContext.api.getMasterScheduleOptions({
            ...filter,
            dateFilterStart: filter.dateFilterStart?.toDate(),
            dateFilterEnd: filter.dateFilterEnd?.toDate(),
            page: page - 1,
            size: pagination.pageSize || DEFAULT_PAGE_SIZE
        });
        filterOptionsAsyncState.setDone(resp.data);
      }
      catch(e: any) {
        console.log("Failed to load filter options", e);
      }
    }

    const resetFilter = () => {
      setFilterFieldsParam(undefined);
      setFilterValueNumbersParam(undefined);
      setFilterValuesParam(undefined);
      setSortFieldsParam(DEFAULT_PAGE_SORT.field);
      setSortDirectionsParam(DEFAULT_PAGE_SORT.direction);

      setCurrentFilter({});
      setCurrentSorting({ columnKey: DEFAULT_PAGE_SORT.field, order: DEFAULT_PAGE_SORT.order });

      setFilter({...LIST_DEFAULT_FILTER});
    }

    const getValidFilters = (filters: Record<string, FilterValue | null> | undefined): Record<string, FilterValue | null> | undefined => {
      if (!filters) return undefined;
      return Object.entries(filters).reduce((acc, [key, value]) => {
        if (value !== null) {
          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, FilterValue>);
    };

    const onFilterSortingChange = (filters: any, sorter: any, pagination: any) => {

      setPageSizeQueryParam(pagination.pageSize);
      setCurrentPageParam(pagination.current);

      const updatedFilterOptions:any = {};

      if (sorter) {
        updatedFilterOptions.sortFields = Array.isArray(sorter) ? sorter.map(col => String(col.columnKey)).join(",") :
          [sorter].map(col => String(col.columnKey)).join(",");
        updatedFilterOptions.sortDirections = Array.isArray(sorter) ? sorter.map(col => (col.order === "ascend" ? "asc" : "desc")).join(",") :
          [sorter].map(col => (col.order === "ascend" ? "asc" : "desc")).join(",");

          setSortFieldsParam(updatedFilterOptions.sortFields);
          setSortDirectionsParam(updatedFilterOptions.sortDirections);
      }

      const validFilters = getValidFilters(filters);

      if (validFilters) {
        updatedFilterOptions.filterFields = Object.entries(validFilters).map(entry => String(entry[0])).join(",");
        updatedFilterOptions.filterValueNumbers = Object.entries(validFilters).map(entry => entry[1]?.length).join(",");
        updatedFilterOptions.filterValues = Object.values(validFilters).flat().join(EXCLAMATION_DELIMITER);

        setFilterFieldsParam(updatedFilterOptions.filterFields);
        setFilterValueNumbersParam(updatedFilterOptions.filterValueNumbers);
        setFilterValuesParam(updatedFilterOptions.filterValues);
      }

      setFilter( { ...filter, ...updatedFilterOptions } );

    }

    function onTableChange(
      pagination: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
      sorter: SorterResult<any> | SorterResult<any>[] | undefined
    ) {
      setPagination(pagination);

      setCurrentSorting(sorter);
      setCurrentFilter(filters);

      if (!Array.isArray(sorter) && sorter?.order == undefined) { sorter = undefined; }
    
      // Handle filters if needed
      onFilterSortingChange(filters, sorter, pagination);
    }
    
    const getCurrentSorting = (columnKey: string) => {
      if (Array.isArray(currentSorting)) {
        return currentSorting?.find(col => col.columnKey === columnKey) ? currentSorting?.find(col => col.columnKey === columnKey)?.order : null;
      }
      else {
        return currentSorting?.columnKey === columnKey ? currentSorting.order : null;
      }
    }

    const getCustomerRequestedShippingdate = (t: ScheduledTruckDto) => {
      return !!t.truck.truckCustomerShippingDate ? 
                dayjs(t.truck.truckCustomerShippingDate).format('MM/DD/YYYY') : 
                (!!t.quote.customerShippingDate ? dayjs(t.quote.customerShippingDate).format('MM/DD/YYYY') : 'Unavailable')
    }

    const handleUpdateTruck = (truck:TruckDto) => {
        if ( !scheduleAsync.data ) return;

        const ndx = scheduleAsync.data?.content.findIndex( s => s.truck.id === truck.id ) || NOT_FOUND;
        if ( ndx === NOT_FOUND ) {
            scheduleAsync.mutate();
        }
        else {
            const s = {
                ...scheduleAsync.data.content[ ndx ],
                truck
            };
            const content = scheduleAsync.data?.content.splice(ndx, 1, s);
            const data = {
                ...scheduleAsync.data,
                content: content
            }
            scheduleAsync.mutate(data);
        }
    }

    const ExportButton = () => {
      const [isExporting, setIsExporting] = useState(false);

      const handleExport = async () => {
        try {

          setIsExporting(true);

          const columns = [
            'Line', 'Serial Number', 'VIN', 'Dealer','Sales','HasPoDocument', 'Customer', 'Model', 'Part Number', 'Destination', 'Cab Type', 'Production Date', 'Ready To Ship Date', 'Ship Date', 'Customer Requested Date', 'Fuel Type', 'Engine Sn', 'Transmission Sn', 'Pilot Inspection', 'Production Status'
          ];
          const headers = getCSVRow(columns);

          const data = ( await configuratorContext.api.listMasterSchedule({
              ...filter,
              dateFilterStart: filter.dateFilterStart?.toDate(),
              dateFilterEnd: filter.dateFilterEnd?.toDate(),
              page: 0,
              size: PAGINATION_MAX_PAGE_SIZE
          }) ).content
            .map((report:ScheduledTruckDto) => 
              getCSVRow([ 
                report.truck.productionLine || '',
                report.truck.truckSerialNumberStr,
                report.truck.vin || '',
                report.quote?.owner.dealerName || '',
                report.quote?.salesTeam?.sales?.[0]?.name || '',
                !!report.quote?.poNumber?.documents?.[0]?.filename ? "true" : "false",
                report.quote.endCustomer?.name || '',
                report.quote.model?.name || '',
                report.quote.partNumberString || '',
                report.quote.shippingDestination?.name || '',
                report.cabStyle || '',
                Utils.coalesce( report.truck.productionDate, report.truck.projectedProductionDate).map(dt => dayjs(dt).format( "MM/DD/YYYY"))?.[0] || "",
                Utils.coalesce( report.truck.readyToShip, report.truck.projectedReadyToShip).map(dt => dayjs(dt).format( "MM/DD/YYYY"))?.[0] || "",
                Utils.coalesce( report.truck.shipDate, report.truck.projectedShipDate).map(dt => dayjs(dt).format( "MM/DD/YYYY"))?.[0] || "",
                getCustomerRequestedShippingdate(report),
                report.fuelType || "",
                report.truck.engineSerialNumber || "",
                report.truck.transmissionSerialNumber || "",
                Utils.coalesce( report.truck.pilotInspectionDate ).map(dt => dayjs(dt).format( "MM/DD/YYYY"))?.[0] || "",
                report.truck.epicorProductionStatus || ""
              ])).flat();

          const csv = [ headers, ...data];
          var blob = new Blob([csv.join('\n')], { type: 'text/csv;charset=utf-8' });
          var url = URL.createObjectURL(blob);
          var a = document.createElement('a');
          a.href = url;
          a.download = 'schedule-export-' + (new Date()) + '.csv';
          document.body.appendChild(a);
          a.click();
        }
        catch (e:any) {
          const errorMsg = intl.formatMessage({ id: e.response?.data.message || e.message });
          notification.error( { message: "Export failed. " + errorMsg });
        }

        setIsExporting(false);

      };

      return <Button type="primary" loading={isExporting} onClick={handleExport}>Export</Button>
    }

    const columns:TableColumnType<ScheduledTruckDto>[] = [
        {
            title: 'Line',
            key: 'productionLine',
            sorter: true,
            sortDirections: ['ascend', 'descend'],
            fixed: true,
            render: (t: ScheduledTruckDto) => {
              return t.truck.productionLine?.split('_').pop() || '';
            }
            
        },
        {
            title: 'Serial Number',
            key: 'truckSerialNumberStr',
            sorter: true,
            sortDirections: ['ascend', 'descend'],
            fixed: true,
            dataIndex: ['truck', 'truckSerialNumberStr'],
            render: (serialNumber, record) => (
              <Link target="_new" to={`/configurator/${record.quote.quoteId}`}>
                  {serialNumber}
              </Link>
            ),
        },
        {
            title: 'Dealer',
            key: 'dealerName',
            sortDirections: ['ascend', 'descend'],
            sorter: true,
            render: (t) => t.quote?.owner.dealerName || '',
        },
        {
            title: 'Customer',
            dataIndex: ['quote', 'endCustomer', 'name'],
            key: 'customerName',
            sortDirections: ['ascend', 'descend'],
            sorter: true,
        },
        {
            title: 'Model',
            dataIndex: ['quote', 'model', 'name'],
            key: 'modelName',
            sorter: true,
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: 'Part Number',
            dataIndex: ['quote', 'partNumberString'],
            key: 'partNumber',
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: 'Destination',
            dataIndex: ['quote', 'shippingDestination', 'name'],
            key: 'destination',
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: 'Cab Type',
            dataIndex: 'cabStyle',
            key: 'cabStyle',
            sorter: true,
            sortDirections: ['ascend', 'descend'],
        },
        {
            key: 'projectedProductionDate',
            title: 'Production Date',
            sorter: true,
            render: (t: ScheduledTruckDto) => t.truck.productionDate ? <span style={{color: "green"}}>{dayjs(t.truck.productionDate).format('MM/DD/YYYY')}</span>
                : t.truck.projectedProductionDate ? <span style={{color: "orange"}}>{dayjs(t.truck.projectedProductionDate).format('MM/DD/YYYY')}</span>
                :<></>
        },
        {
            key: 'projectedReadyToShip',
            title: 'Ready to Ship',
            sorter: true,
            render: (t: ScheduledTruckDto) => t.truck.readyToShip ? <span style={{color: "green"}}>{dayjs(t.truck.readyToShip).format('MM/DD/YYYY')}</span>
                : t.truck.projectedReadyToShip ? <span style={{color: "orange"}}>{dayjs(t.truck.projectedReadyToShip).format('MM/DD/YYYY')}</span>
                :<></>
        },
        {
            key: 'projectedShipDate',
            title: 'Ship Date',
            sorter: true,
            render: (t: ScheduledTruckDto) => t.truck.shipDate ? <span style={{color: "green"}}>{dayjs(t.truck.shipDate).format('MM/DD/YYYY')}</span>
                : t.truck.projectedShipDate ? <span style={{color: "orange"}}>{dayjs(t.truck.projectedShipDate).format('MM/DD/YYYY')}</span>
                :<></>
        },
        {
            title: 'Customer Requested Date',
            key: 'customerRequestedShipDate',
            render: (t) => getCustomerRequestedShippingdate(t)
        },
        {
            title: 'Fuel Type',
            key: 'fuelType',
            dataIndex: ['fuelType'],
            sorter: true,
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: 'Pilot Inspection',
            key: 'pilotInspection',
            sorter: true,
            sortDirections: ['ascend', 'descend'],
            render: (schedule:ScheduledTruckDto) => <div style={{display: "flex", justifyContent: "center"}} >
                <SelectPilotInspectionDate truck={schedule.truck} pilotInspectionsRequired={schedule.pilotInspectionsRequired} onChange={handleUpdateTruck} />
            </div>,
        },
        {
            title: 'Production Status',
            key: 'productionStatus',
            dataIndex: ['truck', 'epicorProductionStatus'],
            sorter: true,
            sortDirections: ['ascend', 'descend'],
        },
    ];

    const getRecordValue = (record: any, columnKey: any) => {
      switch (String(columnKey)) {
        case 'dealerName': return record.quote?.owner?.dealerName;
        case 'customerName': return record.quote?.endCustomer?.name;
        case 'destination': return record.quote?.shippingDestination?.name;
        case 'modelName': return record.quote?.model?.name;
        case 'cabStyle':
        case 'fuelType': return record[columnKey as keyof ScheduledTruckDto];
        case 'productionLine':
        case 'pilotInspection': return record.truck?.[columnKey as keyof ScheduledTruckDto];
        case 'productionStatus': return record.truck?.epicorProductionStatus;
      }
    }

    const columnsWithOptions: TableColumnType<ScheduledTruckDto>[] = useMemo(() => {
      return columns.map(column => {
        const filterOptionKey = column.key as keyof ScheduleFilterOptionDto;
        const columnFilterOptions = filterOptions?.[filterOptionKey]?.options || [];

        let updatedColumn = {...column};
    
        if (columnFilterOptions.length > 0) {
          updatedColumn = {
            ...updatedColumn,
            filters: columnFilterOptions.sort().map(option => ({
              text: filterOptionKey !== "productionLine" 
                ? String(option) 
                : String(option).split('_').pop(),
              value: option,
            })),
            onFilter: (value, record) => {
              const recordValue = getRecordValue(record, column.key);
              return String(recordValue) === String(value);
            },
            filterSearch: true,
            sortOrder: getCurrentSorting(String(column.key)),
            filteredValue: currentFilter[`${column.key}`] || null,
          };
        }

        if (currentSorting?.columnKey === column.key) {
          updatedColumn = {
            ...updatedColumn,
            sortOrder: updatedColumn.sortOrder || getCurrentSorting(String(column.key)),
          };
        }

    
        return updatedColumn;
      });
    }, [filterOptions, currentSorting]);


    const latestUpload= uploadHistoryAsync.data
        ?.filter( u => u.status === MASTER_SCHEDULE_UPLOAD_SUCCESSFUL )
        .sort( (a: UploadHistory, b: UploadHistory) => {
            if (dayjs(a.createdAt).isAfter(dayjs(b.createdAt))) return -1;
            if (dayjs(a.createdAt).isBefore(dayjs(b.createdAt))) return 1;
            return 0;
        })
        .slice(0,1)
        .map( u => u.createdAt && dayjs(u.createdAt).format("M/D/YY, h:mm a") )
        .find(v=>v);


    return (
        <div className="site-layout-background">
              <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: "space-between", gap: '5px' }}>
                    <div style={{marginBottom: ".5rem"}}>
                        <Title level={2} style={{marginBottom: 0}}>Master Schedule</Title>
                        {!!latestUpload && <div>Last updated: {latestUpload}</div>}
                    </div>
                  <Space>
                    {configuratorContext.hasPermission(Permission.MASTER_SCHEDULE_WRITE) && 
                      <Link to="/master-schedule/upload"><Button type="primary">Upload Schedule</Button></Link>}
                    <ExportButton />
                  </Space>
                </div>
                <Segmented
                  options={[ 
                    { label:"List View", value:0, icon: <UnorderedListOutlined /> }, 
                    { label:"Calendar View", value: 1, icon: <CalendarOutlined />  }
                  ]}
                  onChange={handleToggleCalendarView}
                  value={showCalendarView}
                />

                    {showCalendarView ?
                          <ScheduleCalendarView/>
                        : <Skeleton loading={scheduleAsync.isLoading} active style={{marginTop: "1rem"}}>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <TruckFilterControls
                                    loading={scheduleAsync.isLoading}
                                    filter={filter}
                                    onFilterChange={onFilterChange}
                                    tableName={'master-schedule'}
                                    resetFilter={resetFilter}
                                    displayedFields={['dateFilter', 'dealer', 'salespersons', 'endCustomer', 'isPilotInspection']}
                                />
                                <Table 
                                  data-testid="masterScheduleListTable"
                                  loading={scheduleAsync.isLoading}
                                  pagination={{...pagination, total: scheduleAsync.data?.totalElements}}
                                  rowKey={(t: ScheduledTruckDto) => t.truck.id}
                                  dataSource={scheduleAsync.data?.content}
                                  columns={columnsWithOptions} 
                                  onChange={onTableChange}
                                  scroll={{ x: true }}
                                />
                            </Space>
                          </Skeleton>}

            </Space>
        </div>
    );
}
;
const SelectPilotInspectionDate = (props:{
    truck:TruckDto | undefined
    pilotInspectionsRequired:number | undefined
    onChange?: (t:TruckDto) => void
}) => {

    const intl = useIntl();
    const configurator = useContext(ConfiguratorContext);

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [pilotInspectionDate, setPilotInspectionDate] = useState<Date | undefined>();
    const inputRef = useRef<InputRef>(null);

    const updatePilotInspection = async (id: number, date?: Date) : Promise<TruckDto | undefined> => {
        try {
            const resp = await configurator.api.updateTruck( id, {
                pilotInspectionDate:  date ?? null
            } )

            //force type
            return resp.data as TruckDto | undefined;
        }
        catch (e:any) {
            const id = e.response?.data?.message || e.message ;
            const errorMsg = intl.formatMessage({ id });
            notification.error( { message: "Failed to update pilot inspection status at this time. " + errorMsg });
        }
    }

    const handleOpen = () => {
        const pilotInspectionDate =props.truck?.pilotInspectionDate ? dayjs(props.truck.pilotInspectionDate).toDate() : undefined;
        setPilotInspectionDate( pilotInspectionDate );
        setIsOpen(true);
    }
    const handleBlur = () => {
        setIsOpen(false);
    }

    const handleChangePilotInspectionDate = async (value: Dayjs | null, _dateString: string | string[]) => {

        const pilotInspectionDate = value?.toDate();
        setPilotInspectionDate( pilotInspectionDate );

        if ( !props.truck?.id ) return;

        const saved =  await updatePilotInspection( props.truck.id, pilotInspectionDate );
        if ( saved ) {
            props.onChange?.( saved );
        }
    }

    const getDisabledMsg = () => {
        return !configurator.hasPermission(Permission.MASTER_SCHEDULE_PILOT_INSPECTION_WRITE) ? "Additional permissions are required to change this value."
            : undefined;
    }

    const pilotInspectionsRequired =  props.pilotInspectionsRequired || 0;

    const errorStyle = { backgroundColor: "red", color: "white" };
    const selectStyle = { backgroundColor: "rgba( 0, 0, 0, 0.05)", color: "white" }
    const readOnlyStyle = { backgroundColor: "none" }
    const addStyle =
        !configurator.hasPermission(Permission.MASTER_SCHEDULE_PILOT_INSPECTION_WRITE) ? readOnlyStyle
        : ( pilotInspectionsRequired > 0 ) ? selectStyle
        : readOnlyStyle;

    const editStyle =
        !configurator.hasPermission(Permission.MASTER_SCHEDULE_PILOT_INSPECTION_WRITE) ? readOnlyStyle
        : ( pilotInspectionsRequired < 0 ) ? errorStyle
        : readOnlyStyle;

    const btnStyle = {
        borderBottom: "1px solid black",
        borderColor: (editStyle === errorStyle) ? "white": "black",
    };
    const editTooltip = ( editStyle === errorStyle )
        ? `There are more inspections scheduled than required (+${Math.abs(pilotInspectionsRequired)}).`
        : undefined;

    const addTooltip = ( addStyle === selectStyle )
        ? `An inspection date is required for ${pilotInspectionsRequired} trucks.`
        : undefined;

    return <Popover
        open={isOpen}
        afterOpenChange={(open) => {
            if( open ) {
                inputRef.current?.focus();
            }
        }}
        content={<>
            { //@ts-ignore
                <DatePicker placeholder="Inspection Date" ref={inputRef} allowClear
                            value={pilotInspectionDate ? dayjs(pilotInspectionDate) : undefined}
                            onChange={handleChangePilotInspectionDate}
                            onBlur={handleBlur}
                />}
        </>}
    >
        {!!props.truck?.pilotInspectionDate
            ?
            <Tooltip title={editTooltip} placement={"bottom"}>
            <BMButton className="ghostBmButton" type="text"
                        onClick={handleOpen}
                        disabled={!!getDisabledMsg()}
                        onDisabledClick={() => Utils.notifyDisabled(getDisabledMsg())}
                        style={editStyle}
            >
                <span style={btnStyle}>{props.truck?.pilotInspectionDate ? dayjs(props.truck.pilotInspectionDate).format("M/DD/YY") : ""}</span>
            </BMButton>
            </Tooltip>
            : <Tooltip title={addTooltip} placement={"bottom"}>
                <Button style={addStyle} shape="circle" type="text" onClick={handleOpen} />
            </Tooltip>}
    </Popover>
}

export default MasterScheduleView;
